import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCall } from "@/context/CallContext";
import { StreamVideo } from "@/components/StreamVideo";
import { X, Users, VideoOff, Camera, Mic, ShieldCheck, Lock } from "lucide-react";

const PERMISSION_KEY = "orbit_media_ok";

export default function Matchmaking() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { status, online, match, localStream, mediaError, rtcConfig, connect, startLocalMedia, joinQueue, leave, disconnectAll } = useCall();
  const started = useRef(false);
  const [permissionOk, setPermissionOk] = useState(() => localStorage.getItem(PERMISSION_KEY) === "1");

  useEffect(() => {
    if (!user) return;
    if (!user.onboarded) { nav("/onboarding"); return; }
    if (!permissionOk || started.current) return;
    started.current = true;
    (async () => {
      await startLocalMedia();
      await connect();
      joinQueue();
    })();
  }, [user, nav, permissionOk, startLocalMedia, connect, joinQueue]);

  const allow = () => { localStorage.setItem(PERMISSION_KEY, "1"); setPermissionOk(true); };

  useEffect(() => {
    if (status === "call" && match) nav("/chat");
  }, [status, match, nav]);

  if (user && user.onboarded && !permissionOk) {
    return (
      <div className="min-h-screen orbit-bg-grid flex items-center justify-center px-4" data-testid="media-permission-screen">
        <div className="orbit-glass rounded-3xl p-7 sm:p-8 max-w-md w-full orbit-fade-up">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center"><Camera className="w-6 h-6 text-cyan-300" /></div>
            <div className="w-12 h-12 rounded-2xl bg-fuchsia-500/10 border border-fuchsia-500/30 flex items-center justify-center"><Mic className="w-6 h-6 text-fuchsia-300" /></div>
          </div>
          <h1 className="font-display text-2xl font-bold mt-5">Turn on your camera & mic</h1>
          <p className="text-slate-300 text-sm mt-2 leading-relaxed">
            Orbit Hello is face-to-face — your browser will ask for camera and microphone access next. Here's exactly how we use it:
          </p>
          <ul className="mt-4 space-y-3 text-sm">
            <li className="flex gap-3"><Lock className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" /><span><span className="text-slate-100 font-semibold">Never stored.</span> Video and audio stream directly between you and your match. We don't record them or save them on our servers.</span></li>
            <li className="flex gap-3"><ShieldCheck className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" /><span><span className="text-slate-100 font-semibold">Safety scans only.</span> An AI safety check looks at small snapshots of your own camera during calls to block explicit content — snapshots are scored and discarded.</span></li>
            <li className="flex gap-3"><VideoOff className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" /><span><span className="text-slate-100 font-semibold">You're in control.</span> Mute or turn your camera off any time, and Next ends a conversation instantly.</span></li>
          </ul>
          <button onClick={allow} className="orbit-btn-primary w-full mt-6" data-testid="media-permission-allow-btn">Allow camera & microphone</button>
          <button onClick={() => nav("/dashboard")} className="w-full mt-3 text-xs text-slate-400 hover:text-slate-200" data-testid="media-permission-back-btn">Not now</button>
        </div>
      </div>
    );
  }

  const cancel = () => { leave(); disconnectAll(); nav("/dashboard"); };

  return (
    <div className="min-h-screen orbit-bg-grid relative overflow-hidden flex items-center justify-center">
      <div className="relative text-center px-4">
        <div className="relative w-56 h-56 mx-auto mb-8" data-testid="match-searching-radar">
          <div className="orbit-radar-ring" />
          <div className="orbit-radar-ring" style={{ animationDelay: "0.7s" }} />
          <div className="orbit-radar-ring" style={{ animationDelay: "1.4s" }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-28 h-28 rounded-full bg-gradient-to-br from-cyan-400 to-fuchsia-500 p-[3px] orbit-glow-cyan">
              <div className="w-full h-full rounded-full bg-slate-950 overflow-hidden flex items-center justify-center">
                {localStream ? (
                  <StreamVideo stream={localStream} muted mirror className="w-full h-full object-cover" testId="match-self-preview" />
                ) : (
                  <VideoOff className="w-7 h-7 text-slate-500" />
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="text-xs font-mono uppercase tracking-widest text-cyan-300 mb-2" data-testid="match-status-label">
          {status === "connecting" || status === "disconnected" ? "Connecting to Orbit Hello" : "Scanning Orbit Hello"}
        </div>
        <div className="font-display text-3xl font-bold">
          {status === "queue" ? "Waiting for someone…" : "Finding someone…"}
        </div>
        <p className="text-slate-400 mt-3 max-w-sm mx-auto text-sm">
          Matching on your interests · {(user?.interests || []).slice(0, 3).join(" · ")}
        </p>
        <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-2 orbit-glass rounded-full px-3 py-1.5 text-xs font-mono text-emerald-300" data-testid="match-online-counter">
            <Users className="w-3.5 h-3.5" /> {online} online now
          </div>
          <div className={`inline-flex items-center gap-2 orbit-glass rounded-full px-3 py-1.5 text-xs font-mono ${rtcConfig.turn ? "text-cyan-300" : "text-slate-400"}`} data-testid="match-relay-status">
            {rtcConfig.turn ? "TURN relay active" : "STUN only · no relay"}
          </div>
        </div>
        {mediaError && (
          <div className="mt-4 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2 max-w-sm mx-auto" data-testid="match-media-error">
            {mediaError}
          </div>
        )}
        <p className="text-xs text-slate-500 mt-4 max-w-xs mx-auto">If nobody joins within 15s we'll connect you to a demo partner so you can try the flow.</p>
        <button onClick={cancel} className="mt-6 orbit-chip inline-flex items-center gap-2" data-testid="match-cancel-btn">
          <X className="w-3 h-3" /> Cancel
        </button>
      </div>
    </div>
  );
}
